<div class="dashboard-container">
  <div class="page-header">
    <div class="header-content">
      <span class="eyebrow">Wartung</span>
      <h1>Arbeitsübersicht</h1>
      <p class="subtitle">Kunden, Anlagen und offene Termine auf einen Blick.</p>
    </div>
    <div class="header-actions">
      <button mat-stroked-button routerLink="/wartung/kunden">
        <mat-icon>groups</mat-icon>
        Kunden
      </button>
      <button mat-stroked-button routerLink="/wartung/kaufmaennisch">
        <mat-icon>receipt_long</mat-icon>
        Kaufmännisch
      </button>
      <button mat-raised-button color="primary" (click)="openNewCustomerForm()">
        <mat-icon>person_add</mat-icon>
        Neuer Kunde
      </button>
    </div>
  </div>

  <div *ngIf="loading" class="loading-container">
    <mat-spinner diameter="48"></mat-spinner>
    <p>Daten werden geladen...</p>
  </div>

  <ng-container *ngIf="!loading">
    <div class="stats-grid">
      <div class="stat-card"
           *ngFor="let stat of stats"
           [class.clickable]="stat.route"
           (click)="navigateTo(stat.route)">
        <div class="stat-icon" [style.background]="stat.color + '14'" [style.color]="stat.color">
          <mat-icon>{{stat.icon}}</mat-icon>
        </div>
        <div class="stat-info">
          <span class="stat-value">{{stat.value}}</span>
          <span class="stat-label">{{stat.label}}</span>
          <span class="stat-hint" *ngIf="stat.hint">{{stat.hint}}</span>
        </div>
      </div>
    </div>

    <div class="work-area">
      <section class="agenda-panel">
        <div class="panel-header">
          <div>
            <span class="panel-kicker">Arbeitsliste</span>
            <h2>Was als Nächstes zählt</h2>
          </div>
          <button mat-stroked-button *ngIf="canEdit" (click)="startQuickAction('plan')">
            <mat-icon>add_task</mat-icon>
            Wartung planen
          </button>
        </div>

        <div class="filter-row" aria-label="Arbeitsliste filtern">
          <button type="button"
                  class="filter-chip"
                  *ngFor="let filter of agendaFilters"
                  [class.active]="agendaFilter === filter.key"
                  (click)="agendaFilter = filter.key">
            <mat-icon>{{filter.icon}}</mat-icon>
            <span>{{filter.label}}</span>
            <strong>{{getAgendaFilterCount(filter.key)}}</strong>
          </button>
        </div>

        <div *ngIf="filteredAgendaMaintenances.length === 0" class="empty-state compact">
          <mat-icon>event_available</mat-icon>
          <h3>Alles ruhig</h3>
          <p>Für diese Auswahl gibt es aktuell keine offenen Aufträge.</p>
        </div>

        <div class="agenda-list" *ngIf="filteredAgendaMaintenances.length > 0">
          <article class="agenda-item"
                   *ngFor="let m of filteredAgendaMaintenances; trackBy: trackByMaintenanceId"
                   [class.overdue]="isOverdue(m)"
                   [class.soon]="isDueSoon(m) && !isOverdue(m)"
                   (click)="navigateToMaintenance(m)">
            <div class="date-badge">
              <span class="date-day">{{getTargetDate(m) | date:'dd'}}</span>
              <span class="date-month">{{getTargetDate(m) | date:'MMM'}}</span>
            </div>

            <div class="agenda-main">
              <div class="agenda-title-row">
                <span class="type-pill" [class.service]="m.recurrenceType === 'None'">{{getMaintenanceTypeLabel(m)}}</span>
                <h3>{{m.title}}</h3>
              </div>
              <div class="agenda-meta">
                <span><mat-icon>business</mat-icon>{{getCustomerForMaintenance(m)}}</span>
                <span><mat-icon>electrical_services</mat-icon>{{getEquipmentForMaintenance(m)}}</span>
                <span><mat-icon>{{m.assignedUsers?.length ? 'group' : 'person'}}</mat-icon>{{m.assignedUsers?.length ? getTechnicianNames(m) : (m.assignedUser?.userName ?? m.assignedEmployee?.fullName ?? 'Nicht zugewiesen')}}</span>
              </div>
            </div>

            <div class="agenda-state">
              <span class="due-text" [class.danger]="isOverdue(m)">{{getDueText(m)}}</span>
              <span class="status-chip" [ngClass]="getStatusClass(m.status)">
                {{getStatusLabel(m.status, m)}}
              </span>
            </div>

            <div class="agenda-actions">
              <button mat-icon-button matTooltip="Auftrag öffnen" (click)="navigateToMaintenance(m); $event.stopPropagation()">
                <mat-icon>chevron_right</mat-icon>
              </button>
              <button mat-icon-button color="warn" matTooltip="Löschen" (click)="deleteMaintenance(m, $event)">
                <mat-icon>delete_outline</mat-icon>
              </button>
            </div>
          </article>
        </div>
      </section>

      <aside class="side-column">
        <section class="customer-selector">
          <div class="panel-header compact-header">
            <div>
              <span class="panel-kicker">Kundenauswahl</span>
              <h2>Schnell öffnen</h2>
            </div>
            <button mat-icon-button routerLink="/wartung/kunden" matTooltip="Alle Kunden anzeigen">
              <mat-icon>open_in_new</mat-icon>
            </button>
          </div>

          <!-- Hinweis-Banner, wenn eine Schnellaktion auf die Kundenwahl wartet -->
          <div class="pending-action-banner" *ngIf="pendingAction">
            <mat-icon>touch_app</mat-icon>
            <span>Kunde für <strong>{{pendingActionLabel}}</strong> wählen</span>
            <button mat-icon-button (click)="cancelQuickAction()" matTooltip="Abbrechen">
              <mat-icon>close</mat-icon>
            </button>
          </div>

          <div class="customer-search">
            <mat-icon>search</mat-icon>
            <input type="text" placeholder="Kunde oder Anlage suchen" [(ngModel)]="customerSearch">
            <button mat-icon-button *ngIf="customerSearch" (click)="customerSearch = ''" matTooltip="Suche löschen">
              <mat-icon>close</mat-icon>
            </button>
          </div>

          <div class="quick-customer-list" *ngIf="visibleCustomers.length > 0">
            <div class="quick-customer"
                 *ngFor="let customer of visibleCustomers; trackBy: trackByCustomerId"
                 role="button"
                 tabindex="0"
                 (click)="openCustomer(customer)"
                 (keydown.enter)="openCustomer(customer)">
              <span class="customer-avatar" [ngClass]="getCustomerStatusClass(customer)">
                {{getCustomerInitial(customer)}}
              </span>
              <span class="customer-copy">
                <strong>{{customer.name}}</strong>
                <small>{{getCustomerSummary(customer)}}</small>
                <em>{{getEquipmentPreview(customer)}}</em>
              </span>
              <span class="customer-meta" *ngIf="!pendingAction">
                <span class="customer-status" [ngClass]="getCustomerStatusClass(customer)">
                  {{getCustomerStatusLabel(customer)}}
                </span>
                <small>{{getNextMaintenanceLabel(customer)}}</small>
              </span>
              <!-- Direkt-Aktionen je Kunde (nur wenn keine Schnellaktion aktiv ist) -->
              <span class="quick-customer-actions" *ngIf="canEdit && !pendingAction">
                <button mat-icon-button matTooltip="Wartung planen"
                        (click)="openCustomerWithAction(customer,'plan',$event)">
                  <mat-icon>event_repeat</mat-icon>
                </button>
                <button mat-icon-button matTooltip="Einsatz erfassen"
                        (click)="openCustomerWithAction(customer,'service',$event)">
                  <mat-icon>build</mat-icon>
                </button>
              </span>
            </div>
          </div>

          <div *ngIf="visibleCustomers.length === 0" class="empty-state mini">
            <mat-icon>search_off</mat-icon>
            <p>Kein Kunde gefunden.</p>
          </div>
        </section>

        <section class="quick-actions-panel">
          <span class="panel-kicker">Schnellaktionen</span>
          <button mat-stroked-button *ngIf="canEdit" (click)="openNewCustomerForm()">
            <mat-icon>person_add</mat-icon>
            Kunde anlegen
          </button>
          <button mat-stroked-button *ngIf="canEdit"
                  [class.active]="pendingAction === 'plan'"
                  (click)="startQuickAction('plan')">
            <mat-icon>event_repeat</mat-icon>
            Wartung planen
          </button>
          <button mat-stroked-button *ngIf="canEdit"
                  [class.active]="pendingAction === 'service'"
                  (click)="startQuickAction('service')">
            <mat-icon>build</mat-icon>
            Einsatz erfassen
          </button>
          <button mat-stroked-button routerLink="/wartung/kaufmaennisch">
            <mat-icon>receipt_long</mat-icon>
            Abrechnung öffnen
          </button>
        </section>
      </aside>
    </div>
  </ng-container>
</div>
